import React, { memo } from 'react'
import { get, isEmpty } from 'lodash'
import { IconTrash } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { FormField } from '@/components/forms/FormField'
import { FormFieldGrid } from '@/components/forms/FormFieldGrid'
import { TextInput } from '@/components/forms/TextInput'
import { TextArea } from '@/components/forms/TextArea'
import { Checkbox } from '@/components/forms/Checkbox'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import Select from '@/components/forms/Select'
import { OptionsEditor } from '@/components/forms/OptionsEditor/OptionsEditor'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'

import {
  TOOL_REVEALS,
  TOOL_SPEAKERS,
  toolRevealOptions,
  toolSpeakerOptions,
} from './tools.constants'

import {
  getFieldTypeLabel,
  getRowErrorMessages,
  optionsToValues,
  typeTakesValues,
  valuesToOptions,
} from './tools.helpers'

const INLINE_ERROR_KEYS = ['label', 'type', 'description']

const ToolFieldRowComponent = ({
  field,
  index,
  fieldTypes,
  valueTypeOptions,
  toolSpeaker,
  limits,
  focusLabelOnMount,
  onLabelFocused,
  rowErrors,
  rowTouched,
  submitCount,
  onChange,
  onBlur,
  onRemove,
}) => {
  const takesValues = typeTakesValues(fieldTypes, field.type)

  const errorFor = (name) => {
    const message = get(rowErrors, name)
    const shown = submitCount > 0 || get(rowTouched, name)
    return shown && typeof message === 'string' ? message : null
  }
  const labelError = errorFor('label')
  const typeError = errorFor('type')
  const descriptionError = errorFor('description')
  const otherErrors = submitCount > 0 ? getRowErrorMessages(rowErrors, INLINE_ERROR_KEYS) : []

  const handleTypeChange = (option) => {
    const type = option?.value ?? ''
    onChange(index, {
      type,
      values: typeTakesValues(fieldTypes, type) ? field.values || [] : undefined,
    })
  }

  return (
    <div className="tool-field-row" data-testid={`tool-field-row-${index}`}>
      <FormFieldGrid>
        <FormField label="Label" required span={5} error={Boolean(labelError)}>
          <TextInput
            ref={(element) => {
              if (element && focusLabelOnMount) {
                element.focus()
                onLabelFocused()
              }
            }}
            value={field.label}
            placeholder="e.g. Date of birth"
            aria-label={`Field ${index + 1} label`}
            onChange={(event) => onChange(index, { label: event.target.value })}
            onBlur={() => onBlur(index, 'label')}
            data-testid={`tool-field-label-${index}`}
          />
          {labelError && <ErrorMessage content={labelError} />}
          <span className="tool-field-key" data-testid={`tool-field-key-${index}`}>
            {isEmpty(field.key) ? 'Key is set from the label' : `Key: ${field.key}`}
          </span>
        </FormField>
        <FormField label="Type" required span={4} error={Boolean(typeError)}>
          <Select
            options={valueTypeOptions}
            value={field.type}
            isClearable={false}
            placeholder="Select a type"
            inputId={`tool-field-type-${index}`}
            aria-label={`Field ${index + 1} type`}
            onChange={handleTypeChange}
            data-testid={`tool-field-type-${index}`}
          />
          {typeError && <ErrorMessage content={typeError} />}
          {!isEmpty(field.typeFallbackFrom) && (
            <ErrorMessage
              warning
              content={`The ${getFieldTypeLabel(fieldTypes, field.typeFallbackFrom)} type is no longer available. This field saves as Free text unless you pick another type.`}
              data-testid={`tool-field-type-fallback-${index}`}
            />
          )}
        </FormField>
        <FormField label="Required" span={3}>
          <Checkbox
            toggle
            checked={Boolean(field.required)}
            onChange={(event, data) => onChange(index, { required: data.checked })}
            data-testid={`tool-field-required-${index}`}
          />
        </FormField>
        <FormField>
          <Button
            square
            basic
            small
            aria-label="Remove field"
            tooltip="Remove field"
            onClick={() => onRemove(index)}
            data-testid={`tool-field-remove-${index}`}
          >
            <IconTrash />
          </Button>
        </FormField>
      </FormFieldGrid>
      <FormFieldGrid>
        <FormField label="Speaker" span={6}>
          <InteractiveTooltip
            block
            hide={isEmpty(toolSpeaker)}
            content="Set for every field by the Speaker above. Choose Per field there to pick one here."
          >
            <Select
              options={toolSpeakerOptions}
              value={isEmpty(toolSpeaker) ? field.speaker || TOOL_SPEAKERS.CUSTOMER : toolSpeaker}
              isClearable={false}
              isDisabled={!isEmpty(toolSpeaker)}
              inputId={`tool-field-speaker-${index}`}
              aria-label={`Field ${index + 1} speaker`}
              onChange={(option) => onChange(index, { speaker: option.value })}
              data-testid={`tool-field-speaker-${index}`}
            />
          </InteractiveTooltip>
        </FormField>
        <FormField label="Reveal" span={6}>
          <Select
            options={toolRevealOptions}
            value={field.reveal || TOOL_REVEALS.FULL}
            isClearable={false}
            inputId={`tool-field-reveal-${index}`}
            aria-label={`Field ${index + 1} reveal`}
            onChange={(option) => onChange(index, { reveal: option.value })}
            data-testid={`tool-field-reveal-${index}`}
          />
        </FormField>
      </FormFieldGrid>
      <FormFieldGrid>
        <FormField label="Description" span={12} error={Boolean(descriptionError)}>
          <TextArea
            rows={2}
            value={field.description || ''}
            aria-label={`Field ${index + 1} description`}
            onChange={(event) => onChange(index, { description: event.target.value })}
            onBlur={() => onBlur(index, 'description')}
            data-testid={`tool-field-description-${index}`}
          />
          {descriptionError && <ErrorMessage content={descriptionError} />}
        </FormField>
      </FormFieldGrid>
      {takesValues && (
        <OptionsEditor
          options={valuesToOptions(field.values)}
          onChange={(options) => onChange(index, { values: optionsToValues(options) })}
          maxOptions={limits.values}
          testIdPrefix={`tool-field-${index}`}
        />
      )}
      {otherErrors.map((message) => (
        <ErrorMessage key={message} content={message} />
      ))}
    </div>
  )
}

// memo skips re-rendering a row whose props have not changed. Without it every keystroke in
// the form re-rendered all 24 rows (three react-selects and an options editor each), which is
// what made typing slow. It only works while every prop keeps its identity between renders:
// AutoCaptureFields passes index-based handlers wrapped in useCallback, this row's own slice
// of errors and touched (not the whole objects), and a memoized option list. Passing an inline
// closure or a freshly built array here quietly turns the memo off again.
export const ToolFieldRow = memo(ToolFieldRowComponent)
